import React, { useContext, useEffect, useState } from "react";
import useOrders from "../../../services/Order/useOrder";
import useDelivery from "../../../services/Order/useDelivery";
import { RefreshCw, Settings, Store, X } from "lucide-react";
import useProfileDashboard from "../../../services/Settings/useProfileDashboard";
import { GlobalContext } from "../../../Context/GlobalContext";
import { KitchenPrint } from "./KitchenPrint";
import DispatchOrderModel from "./DispatchOrderModel";
import { toast } from "react-toastify";
import PrintFull from "./PrintFull";
import { generatePNG } from "./ReceiptGenerator";
import { useNavigate } from "react-router-dom";
import CurrentOrdersTable from "./CurrentOrderstable";
import OrderHistoryTable from "./OrderHistoryTable";
import { exportAnyJsonToExcel } from "../../../utils/exportAnyJsonToExcel";
import { printSummary } from "./PrintSummary";
import { printMergedSummary } from "./PrintMergeSummary";
import html2canvas from "html2canvas";
import { generateReceiptTharmalPrinter } from "./DownloadReceipt";
import TakeawayModal from "./TakeawayModal";
import Schedule from "./Schedule";
import LoadingScreen from "../../../utils/LoadingScreen";
import OrderActionModals from "./OrderActionModals";
import OrderFilter from "./OrderFilter";
import CreateBulkOrder from "./CreateBulkOrder";

const CashCollectionModal = ({ order, onClose, onConfirm }) => {
  const [paymentMode, setPaymentMode] = useState("Cash");

  const [collectedBy, setCollectedBy] = useState("Restaurant");

  const [loading, setLoading] = useState(false);

  const { userID } = useContext(GlobalContext);

  const isEmptyApiData = (res) => {
    const data = res?.response || res?.data || [];

    if (Array.isArray(data)) {
      return data.length === 0;
    }

    return !data;
  };

  const isApiSuccess = (res) => {
    return res?.responseCode === "00";
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      const payload = {
        userid: userID,
        orderid: order.orderid,
        productid: "string",
        status: "cash_collected",
        payment: paymentMode === "Cash" ? "cod" : "card",
        paymentCollectedBy: collectedBy,
        remarks: collectedBy,
        time: "string",
      };

      await onConfirm(payload);

      onClose();
    } catch (error) {
      console.error("Cash collection error:", error);

      toast.error("Cash collection failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center montserrat-font-1 justify-center bg-black bg-opacity-40"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 montserrat-font-1"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b montserrat-font-1">
            <h2 className="text-base font-semibold text-gray-800">
              Cash Collection
            </h2>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex flex-col items-center py-6 border-b">
            <span className="text-4xl font-bold text-green-600">
              ₹{parseFloat(order.totalOrderPrice || 0).toFixed(2)}
            </span>

            <span className="text-sm text-gray-500 mt-1">
              Order Amount to Collect
            </span>
          </div>

          <div className="px-6 pt-5">
            <p className="text-sm font-semibold text-gray-700 mb-3">
              Payment Mode
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setPaymentMode("Cash")}
                className={`px-5 py-2 rounded-lg text-sm font-medium border transition ${
                  paymentMode === "Cash"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-blue-500 border-blue-300"
                }`}
              >
                Cash
              </button>

              <button
                onClick={() => setPaymentMode("Card")}
                className={`px-5 py-2 rounded-lg text-sm font-medium border transition ${
                  paymentMode === "Card"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-blue-500 border-blue-300"
                }`}
              >
                Card Payment
              </button>
            </div>
          </div>

          <div className="px-6 pt-5 pb-6 montserrat-font-1">
            <p className="text-sm font-semibold text-gray-700 mb-3">
              Collected By
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setCollectedBy("Restaurant")}
                className={`px-5 py-2 rounded-lg text-sm font-medium border transition ${
                  collectedBy === "Restaurant"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-400 border-gray-200"
                }`}
              >
                Restaurant
              </button>

              <button
                onClick={() => setCollectedBy("Delivery Partner")}
                className={`px-5 py-2 rounded-lg text-sm font-medium border transition ${
                  collectedBy === "Delivery Partner"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-400 border-gray-200"
                }`}
              >
                Delivery Partner
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t montserrat-font-1">
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
            >
              Cancel
            </button>

            <button
              onClick={handleConfirm}
              disabled={loading}
              className="px-5 py-2 rounded-lg text-sm font-semibold bg-green-500 text-white hover:bg-green-600 transition disabled:opacity-60"
            >
              {loading
                ? "Processing..."
                : `Confirm & Collect ₹${parseFloat(
                    order.totalOrderPrice || 0,
                  ).toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const getDiningStatusLabel = (order) => {
  const status = order.status?.toLowerCase().trim();

  const tableNo = order.table_no;

  if (status === "paid") {
    const mode = order.payment_mode;

    const modeFormatted = mode.charAt(0).toUpperCase() + mode.slice(1);

    return {
      label: `${modeFormatted} Paid & Preparing`,
      className: "text-green-600 font-semibold text-sm",
    };
  }

  return {
    label: `Table ${tableNo || ""} Active`,
    className:
      "bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold",
  };
};

const hasScheduleDate = (order) => {
  const scheduledDate =
    order?.scheduled_date ||
    order?.schedule_date ||
    order?.scheduleDate ||
    order?.scheduledDate ||
    order?.schedule_time ||
    order?.scheduled_time;

  return Boolean(String(scheduledDate || "").trim());
};

const isScheduleOrder = (order) => {
  const orderType = (order?.orderType || order?.order_type || "")
    .toLowerCase()
    .trim();

  const status = order?.status?.toLowerCase()?.trim();

  return (
    orderType === "schedule" ||
    orderType === "scheduled" ||
    status === "schedule" ||
    status === "scheduled" ||
    hasScheduleDate(order)
  );
};

const OrdersPage = () => {
  const {
    orders,
    orderHistory,
    getNewOrders,
    getOrderHistory,
    updateOrderStatus,
    getOrderDetails,
    assignDeliveryPartner,
    sendReceiptAPI,
    getCatalogueStatus,
    updateCatalogueStatus,
    getBasicSummaryReport,
    getMergedSummaryReport,
    getOrderDetailsSummary,
    notifyDriver,
    updateScheduleOrder,
  } = useOrders();

  const { deliveryPersons, getDeliveryPersons } = useDelivery();
  const { fetchReceiptDetails } = useProfileDashboard();

  const [activeTab, setActiveTab] = useState("current");
  const [filter, setFilter] = useState("all");
  const { userID } = useContext(GlobalContext);

  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [historyStartDate, setHistoryStartDate] = useState(today);
  const [historyEndDate, setHistoryEndDate] = useState(today);

  const [search, setSearch] = useState("");
  const [orderAccepting, setOrderAccepting] = useState(true);
  const [orderDetails, setOrderDetails] = useState({});
  const [openRowCurrent, setOpenRowCurrent] = useState(null);
  const [openRowHistory, setOpenRowHistory] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [reciptDetails, setreciptDetails] = useState({});
  const [actionModalOrder, setActionModalOrder] = useState(null);
  const [cancelModalOrder, setCancelModalOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [openCancelDropdown, setOpenCancelDropdown] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [deliverModalOrder, setDeliverModalOrder] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedSummaryDate, setSelectedSummaryDate] = useState("");
  const [showMergedModal, setShowMergedModal] = useState(false);
  const [selectedMergedDate, setSelectedMergedDate] = useState("");
  const [newOrderIds, setNewOrderIds] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  const [takeawayOrder, setTakeawayOrder] = useState(null);
  const [dispatchedOrderIds, setDispatchedOrderIds] = useState([]);
  const [summaryActionLoading, setSummaryActionLoading] = useState(false);
  const [mergedSummaryActionLoading, setMergedSummaryActionLoading] =
    useState(false);

  const [cashModalOrder, setCashModalOrder] = useState(null);

  const navigate = useNavigate();

  const cancelReasons = [
    "Menu Item Out of Stock",
    "Customer Cancelled",
    "Restaurant Closed",
    "Payment Issue",
  ];

  const handleRowClickCurrent = async (index, orderid) => {
    if (openRowCurrent === index) {
      setOpenRowCurrent(null);
      setOrderDetails({});
      setOrderDetailsLoading(false);
      return;
    }

    try {
      setOpenRowCurrent(index);
      setOpenRowHistory(null);

      setOrderDetails({});
      setOrderDetailsLoading(true);

      const response = await getOrderDetails(orderid);

      const data = Array.isArray(response)
        ? response[0]
        : Array.isArray(response?.response)
          ? response.response[0]
          : response?.response && typeof response.response === "object"
            ? response.response
            : response;

      setOrderDetails(data || {});
    } catch (error) {
      console.error("Current order details error:", error);

      setOrderDetails({});

      toast.error("Unable to load order details");
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const handleRowClickHistory = async (index, orderid) => {
    if (openRowHistory === index) {
      setOpenRowHistory(null);
      setOrderDetails({});
      setOrderDetailsLoading(false);
      return;
    }

    try {
      setOpenRowHistory(index);
      setOpenRowCurrent(null);

      setOrderDetails({});
      setOrderDetailsLoading(true);

      const response = await getOrderDetails(orderid);

      const data = Array.isArray(response)
        ? response[0]
        : Array.isArray(response?.response)
          ? response.response[0]
          : response?.response && typeof response.response === "object"
            ? response.response
            : response;

      setOrderDetails(data || {});
    } catch (error) {
      console.error("History order details error:", error);

      setOrderDetails({});

      toast.error("Unable to load order details");
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const sourceData =
    filter === "rejected"
      ? orderHistory
      : orders.filter((o) => {
          const status = o.status?.toLowerCase()?.trim();

          if (
            [
              "dispatched",
              "ready_for_takeaway",
              "ready_for_dinning",
              "delivered",
              "completed",
              "rejected",
              "refunded",
            ].includes(status)
          ) {
            return false;
          }

          return true;
        });

  const filteredOrders = sourceData.filter((item) => {
    const status = item.status?.toLowerCase().trim();

    const type = (item.orderType || item.order_type || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_");

    const paymentMode = item.payment_mode?.toLowerCase();

    const paymentStatus = item.payment_status?.toLowerCase();

    const matchFilter =
      filter === "all" ||
      (filter === "active" &&
        ["paid", "accepted", "received"].includes(status)) ||
      (filter === "rejected" && ["rejected", "refunded"].includes(status)) ||
      (filter === "dining" && type === "dining");

    const matchSearch =
      !search ||
      item.orderid?.toString().includes(search) ||
      item.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      item.customer_mobile?.includes(search) ||
      item.table_no?.toString().includes(search);

    const matchPayment =
      paymentFilter === "all" ||
      (paymentFilter === "cod" && paymentMode === "cod") ||
      (paymentFilter === "upi" &&
        paymentMode === "upi" &&
        paymentStatus === "paid") ||
      (paymentFilter === "unpaid" && paymentStatus !== "paid");

    const matchOrderType =
      orderTypeFilter === "all" || type === orderTypeFilter;

    return matchFilter && matchSearch && matchPayment && matchOrderType;
  });

  const completedStatuses = [
    "dispatched",
    "ready_for_takeaway",
    "ready_for_dinning",
    "delivered",
    "completed",
    "rejected",
    "refunded",
  ];

  const getScheduleDateTime = (order) => {
    return (
      order?.scheduled_date ||
      order?.schedule_date ||
      order?.scheduleDate ||
      order?.scheduledDate ||
      order?.schedule_time ||
      order?.scheduled_time ||
      ""
    );
  };

  const parseScheduleDateTime = (scheduledDate) => {
    if (!scheduledDate) {
      return null;
    }

    const value = String(scheduledDate).trim();

    if (!value) {
      return null;
    }

    const [datePart, timePart] = value.split(" ");

    if (!datePart || !timePart) {
      return null;
    }

    const [year, month, day] = datePart.split("-").map(Number);

    const [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (
      !year ||
      !month ||
      !day ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return null;
    }

    const date = new Date(year, month - 1, day, hours, minutes, seconds || 0);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  };

  const parseDeliveryTimeToMilliseconds = (deliveryTime) => {
    const value = String(deliveryTime ?? "").trim();

    if (!value) {
      return 0;
    }

    if (value.includes(".")) {
      const [minutePart = "0", secondPart = "0"] = value.split(".");

      const minutes = Number(minutePart) || 0;

      let seconds = Number(secondPart) || 0;

      if (secondPart.length === 1) {
        seconds *= 10;
      }

      return minutes * 60 * 1000 + seconds * 1000;
    }

    const minutes = Number(value);

    if (!Number.isFinite(minutes)) {
      return 0;
    }

    return minutes * 60 * 1000;
  };

  const getScheduleDispatchActiveTime = (order) => {
    const scheduledDate = getScheduleDateTime(order);

    if (!scheduledDate) {
      return null;
    }

    const scheduleDateTime = parseScheduleDateTime(scheduledDate);

    if (!scheduleDateTime) {
      return null;
    }

    const deliveryTimeMs = parseDeliveryTimeToMilliseconds(
      order?.delivery_time,
    );

    const additionalBufferMs = 10 * 60 * 1000;

    return scheduleDateTime.getTime() - deliveryTimeMs - additionalBufferMs;
  };

  const getNumericOrderId = (order) => {
    const orderId = String(order?.orderid ?? "");

    const numericValue = Number(orderId.replace(/\D/g, ""));

    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const compareScheduleOrdersByTime = (a, b) => {
    const now = currentTime.getTime();

    const dispatchTimeA = getScheduleDispatchActiveTime(a);
    const dispatchTimeB = getScheduleDispatchActiveTime(b);

    const hasDispatchTimeA = Number.isFinite(dispatchTimeA);
    const hasDispatchTimeB = Number.isFinite(dispatchTimeB);

    if (hasDispatchTimeA && hasDispatchTimeB) {
      const differenceA = Math.abs(dispatchTimeA - now);
      const differenceB = Math.abs(dispatchTimeB - now);

      if (differenceA !== differenceB) {
        return differenceA - differenceB;
      }

      return getNumericOrderId(b) - getNumericOrderId(a);
    }

    if (hasDispatchTimeA && !hasDispatchTimeB) {
      return -1;
    }

    if (!hasDispatchTimeA && hasDispatchTimeB) {
      return 1;
    }

    return getNumericOrderId(b) - getNumericOrderId(a);
  };

  const sortOnlyScheduleOrders = (list) => {
    const scheduleOnly = list
      .filter((order) => isScheduleOrder(order))
      .sort(compareScheduleOrdersByTime);

    let scheduleIndex = 0;

    return list.map((order) => {
      if (!isScheduleOrder(order)) {
        return order;
      }

      const sortedScheduleOrder = scheduleOnly[scheduleIndex];

      scheduleIndex += 1;

      return sortedScheduleOrder || order;
    });
  };

  const newOrders = filteredOrders.filter((order) =>
    newOrderIds.includes(order.orderid),
  );

  const existingOrders = filteredOrders.filter(
    (order) => !newOrderIds.includes(order.orderid),
  );

  const sortedOrders = [
    ...sortOnlyScheduleOrders(newOrders),
    ...sortOnlyScheduleOrders(existingOrders),
  ];

  const scheduleOrders = [...filteredOrders]
    .filter((order) => {
      const status = order.status?.toLowerCase()?.trim();

      return isScheduleOrder(order) && !completedStatuses.includes(status);
    })
    .sort(compareScheduleOrdersByTime);

  const handleCancel = async (orderid) => {
    await updateOrderStatus(orderid, "rejected");

    getNewOrders(today, today);
  };

  const handleCashCollect = async (payload) => {
    await updateOrderStatus(payload);

    await getNewOrders(startDate, endDate);

    if (activeTab === "history") {
      await getOrderHistory(historyStartDate, historyEndDate);
    }
  };

  const selectedDeliveryPerson = deliveryPersons.find(
    (p) => p.id === orderDetails?.deliveryPersonId,
  );

  const getTimeElapsed = (orderDate, status, isHistory = false) => {
    const s = status?.toLowerCase()?.trim();

    if (s !== "paid") {
      return {
        time: "00:00",
        isLate: false,
      };
    }

    const [datePart, timePart, period] = orderDate.split(" ");

    const [day, month, year] = datePart.split("/").map(Number);

    let [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (period === "PM" && hours !== 12) {
      hours += 12;
    }

    if (period === "AM" && hours === 12) {
      hours = 0;
    }

    const orderTime = new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      seconds,
    ).getTime();

    const now = new Date().getTime();

    const diff = now - orderTime;

    const totalMinutes = Math.floor(diff / (1000 * 60));

    const totalHours = Math.floor(totalMinutes / 60);

    if (isHistory) {
      return {
        time: totalHours > 0 ? `${totalHours} hr` : `${totalMinutes} min`,
        isLate: totalHours >= 1,
      };
    }

    return {
      time: totalHours > 0 ? `${totalHours} hr` : `${totalMinutes} min`,
      isLate: totalHours >= 1,
    };
  };

  const filteredHistory = orderHistory.filter((item) => {
    const searchValue = search.toLowerCase();

    return (
      !search ||
      item.orderid?.toString().includes(searchValue) ||
      item.customer_name?.toLowerCase().includes(searchValue) ||
      item.customer_mobile?.includes(searchValue) ||
      item.table_no?.toString().includes(searchValue)
    );
  });

  const handlePrintClick = async (order) => {
    try {
      let fullAddress = order?.address_line || "";

      try {
        const parsed =
          typeof order?.address_json === "string"
            ? JSON.parse(order.address_json || "{}")
            : order?.address_json || {};

        const addressObj = parsed?.values || {};

        const addressParts = [
          addressObj.address,
          addressObj.landmark_area,
          addressObj.building_name,
          addressObj.house_number,
          addressObj.tower_number,
          addressObj.floor_number,
          addressObj.city,
          addressObj.state,
          addressObj.in_pin_code,
        ]
          .filter(Boolean)
          .map((value) => String(value).trim());

        if (addressParts.length > 0) {
          fullAddress = addressParts.join(", ");
        }
      } catch (error) {
        console.error("Address parse error:", error);
      }

      const details = await getOrderDetails(order.orderid);

      if (!details) {
        toast.error("Failed to fetch order details");

        return;
      }

      PrintFull(
        reciptDetails,
        details,
        fullAddress || details?.address_line || "-",
        userID,
      );
    } catch (err) {
      console.error(err);

      toast.error("Print failed");
    }
  };

  const handleDownloadPNG = async (order) => {
    try {
      const addressObj = JSON.parse(order.address_json || "{}")?.values || {};

      const fullAddress = [
        addressObj.address,
        addressObj.landmark_area,
        addressObj.building_name,
        addressObj.house_number,
        addressObj.floor_number,
        addressObj.city,
        addressObj.state,
        addressObj.in_pin_code,
      ]
        .filter(Boolean)
        .join(", ");

      const details = await getOrderDetails(order.orderid);

      const html = generateReceiptTharmalPrinter(
        details,
        fullAddress,
        reciptDetails,
        reciptDetails,
      );

      const container = document.createElement("div");

      container.innerHTML = html;

      document.body.appendChild(container);

      const element = container.querySelector("#receipt");

      if (!element) {
        throw new Error("Receipt element not found");
      }

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });

      const image = canvas.toDataURL("image/png");

      const link = document.createElement("a");

      link.href = image;

      link.download = `receipt_${order.orderid}.png`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      document.body.removeChild(container);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDirectClick = async (order) => {
    const orderId =
      typeof order?.orderid === "object"
        ? order.orderid.orderid
        : order?.orderid;

    try {
      if (!orderId) {
        toast.error("Order ID missing");

        return;
      }

      if (dispatchedOrderIds.includes(orderId)) {
        return;
      }

      setDispatchedOrderIds((prev) => [...prev, orderId]);

      const normalizedOrderType = (order?.orderType || order?.order_type || "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "_");

      const finalStatus =
        normalizedOrderType === "office_order" ||
        normalizedOrderType === "car_order"
          ? "ready_for_takeaway"
          : "dispatched";

      const payload = {
        userid: userID,
        orderid: orderId,
        productid: "",
        status: finalStatus,
        time: "",
        payment: order?.payment_mode || "",
      };

      const res = await updateOrderStatus(payload);

      await Promise.all([
        getNewOrders(startDate, endDate),
        getOrderHistory(historyStartDate, historyEndDate),
      ]);

      if (res?.responseCode !== "00") {
        toast.error(res?.responseMessage || "Failed to dispatch order");

        setDispatchedOrderIds((prev) => prev.filter((id) => id !== orderId));

        return;
      }

      toast.success(
        normalizedOrderType === "office_order"
          ? "Office order dispatched successfully"
          : "Order dispatched successfully",
      );

      await Promise.all([
        getNewOrders(startDate, endDate),
        getOrderHistory(startDate, endDate),
      ]);

      setDispatchedOrderIds((prev) => prev.filter((id) => id !== orderId));
    } catch (err) {
      console.error("Dispatch error:", err);

      toast.error("Something went wrong while dispatching order");

      setDispatchedOrderIds((prev) => prev.filter((id) => id !== orderId));
    }
  };

  const handleSendReceipt = async (order) => {
    try {
      const addressObj = JSON.parse(order?.address_json || "{}")?.values || {};

      const fullAddress = [
        addressObj.address,
        addressObj.landmark_area,
        addressObj.building_name,
        addressObj.house_number,
        addressObj.floor_number,
        addressObj.city,
        addressObj.state,
        addressObj.in_pin_code,
      ]
        .filter(Boolean)
        .map((v) => v.toString().trim())
        .join(", ");

      const imageData = await generatePNG(
        order,
        fullAddress,
        reciptDetails,
        reciptDetails,
      );

      await sendReceiptAPI({
        userid: userID,
        secret: "string",
        order_id: order.orderid,
        image_data: imageData,
        type: "IMG",
      });
    } catch (err) {
      console.error("sendReceipt error", err);
    }
  };

  const handleCatalogueToggle = async () => {
    try {
      const newStatus = orderAccepting ? "hide" : "show";

      const res = await updateCatalogueStatus(newStatus);

      if (res?.responseCode === "00") {
        setOrderAccepting(!orderAccepting);

        toast.success(
          newStatus === "show"
            ? "Order accepting enabled"
            : "Order accepting disabled",
        );
      } else {
        toast.error("Failed to update status");
      }
    } catch (err) {
      console.error(err);

      toast.error("Something went wrong");
    }
  };

  const openDeliverModal = (order) => {
    setDeliverModalOrder(order);
  };

  const handleConfirmDelivered = async () => {
    try {
      const payload = {
        userid: userID,
        orderid: deliverModalOrder.orderid,
        productid: "",
        status: "delivered",
        time: "",
        payment: "",
      };

      const res = await updateOrderStatus(payload);

      if (res?.responseCode === "00") {
        toast.success("Order marked as delivered");

        getCatalogueStatus();

        await getNewOrders(startDate, endDate);

        if (activeTab === "history") {
          await getOrderHistory(startDate, endDate);
        }
      } else {
        toast.error("Failed to update order");
      }

      setDeliverModalOrder(null);
    } catch (err) {
      console.error(err);

      toast.error("Something went wrong");
    }
  };

  const handleRefresh = async () => {
    try {
      setLoadingOrders(true);

      if (activeTab === "history") {
        await getOrderHistory(historyStartDate, historyEndDate);
      } else {
        await getNewOrders(startDate, endDate);
      }
    } catch (error) {
      console.error("Refresh error:", error);

      toast.error("Unable to refresh orders");
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleHistorySearch = async () => {
    try {
      setLoadingOrders(true);

      await getOrderHistory(historyStartDate, historyEndDate);
    } catch (error) {
      console.error("History search error:", error);

      toast.error("Unable to fetch order history");
    } finally {
      setLoadingOrders(false);
    }
  };

  const getLatestReceiptDetails = async () => {
    try {
      const res = await fetchReceiptDetails(userID);

      const receipt =
        res?.response?.[0] ||
        res?.data?.[0] ||
        res?.response ||
        res?.data ||
        res ||
        null;

      if (receipt && typeof receipt === "object") {
        setreciptDetails(receipt);

        return receipt;
      }

      console.warn("Receipt API returned no valid receipt object");

      return reciptDetails || {};
    } catch (error) {
      console.error("Receipt API error:", error);

      return reciptDetails || {};
    }
  };

  const handlePrintSummary = async () => {
    try {
      if (summaryActionLoading) {
        return;
      }

      if (!selectedSummaryDate) {
        toast.error("Please select date");

        return;
      }

      setSummaryActionLoading(true);

      const data = await getBasicSummaryReport(
        selectedSummaryDate,
        selectedSummaryDate,
      );

      if (!data) {
        toast.error("Unable to fetch summary");

        return;
      }

      if (data?.responseCode && data.responseCode !== "00") {
        toast.error(data?.responseMessage || "Unable to fetch summary");

        return;
      }

      if (!data.response || data.response.length === 0) {
        toast.info("No summary data found");

        return;
      }

      const latestReceiptDetails = await getLatestReceiptDetails();

      printSummary(
        data.response || [],
        data.data2 || {},
        latestReceiptDetails,
        userID,
      );

      setShowSummaryModal(false);
    } catch (error) {
      console.error("Summary print error:", error);

      toast.error("Unable to print summary");
    } finally {
      setSummaryActionLoading(false);
    }
  };

  const handleDownloadSummaryExcel = async () => {
    try {
      if (summaryActionLoading) {
        return;
      }

      if (!selectedSummaryDate) {
        toast.error("Please select date");

        return;
      }

      setSummaryActionLoading(true);

      const data = await getBasicSummaryReport(
        selectedSummaryDate,
        selectedSummaryDate,
      );

      if (!data) {
        toast.error("Unable to fetch summary");

        return;
      }

      if (data?.responseCode && data.responseCode !== "00") {
        toast.error(data?.responseMessage || "Unable to fetch summary");

        return;
      }

      const responseData = Array.isArray(data) ? data : data.response || [];

      if (responseData.length === 0) {
        toast.info("No summary data found");

        return;
      }

      exportAnyJsonToExcel({
        data: responseData,
        filePrefix: `Summary_Report_${selectedSummaryDate}`,
        sheetName: "Summary Report",
      });

      setShowSummaryModal(false);
    } catch (error) {
      console.error("Summary excel error:", error);

      toast.error("Unable to download summary excel");
    } finally {
      setSummaryActionLoading(false);
    }
  };

  const handlePrintMergedSummary = async () => {
    try {
      if (mergedSummaryActionLoading) {
        return;
      }

      if (!selectedMergedDate) {
        toast.error("Please select date");

        return;
      }

      setMergedSummaryActionLoading(true);

      const data = await getMergedSummaryReport(
        selectedMergedDate,
        selectedMergedDate,
      );

      if (!data) {
        toast.error("Unable to fetch merged summary");

        return;
      }

      if (data?.responseCode && data.responseCode !== "00") {
        toast.error(data?.responseMessage || "Unable to fetch merged summary");

        return;
      }

      const responseData = Array.isArray(data) ? data : data.response || [];

      if (responseData.length === 0) {
        toast.info("No merged summary data found");

        return;
      }

      let formattedData;

      if (Array.isArray(data)) {
        formattedData = {
          response: data,
          fromDate: selectedMergedDate,
          toDate: selectedMergedDate,
          whatsapp: {},
          pos: {},
          data2: {},
        };
      } else {
        formattedData = {
          response: data.response || [],
          fromDate: data.fromDate || selectedMergedDate,
          toDate: data.toDate || selectedMergedDate,
          whatsapp: data.whatsapp || {},
          pos: data.pos || {},
          data2: data.data2 || {},
        };
      }

      const latestReceiptDetails = await getLatestReceiptDetails();

      printMergedSummary(formattedData, latestReceiptDetails, userID);

      setShowMergedModal(false);
    } catch (error) {
      console.error("Merged summary print error:", error);

      toast.error("Unable to print merged summary");
    } finally {
      setMergedSummaryActionLoading(false);
    }
  };

  const handleDownloadMergedSummaryExcel = async () => {
    try {
      if (mergedSummaryActionLoading) {
        return;
      }

      if (!selectedMergedDate) {
        toast.error("Please select date");

        return;
      }

      setMergedSummaryActionLoading(true);

      const data = await getMergedSummaryReport(
        selectedMergedDate,
        selectedMergedDate,
      );

      if (!data) {
        toast.error("Unable to fetch merged summary");

        return;
      }

      if (data?.responseCode && data.responseCode !== "00") {
        toast.error(data?.responseMessage || "Unable to fetch merged summary");

        return;
      }

      const responseData = Array.isArray(data) ? data : data.response || [];

      if (responseData.length === 0) {
        toast.info("No merged summary data found");

        return;
      }

      const excelData = responseData.map((item) => ({
        ...item,
        report_from_date: selectedMergedDate,
        report_to_date: selectedMergedDate,
      }));

      exportAnyJsonToExcel({
        data: excelData,
        filePrefix: `Merged_Summary_${selectedMergedDate}`,
        sheetName: "Merged Summary",
      });

      setShowMergedModal(false);
    } catch (error) {
      console.error("Merged summary excel error:", error);

      toast.error("Unable to download merged summary excel");
    } finally {
      setMergedSummaryActionLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "current" && activeTab !== "schedule") {
      return;
    }

    const fetchData = async () => {
      setLoadingOrders(true);

      await getNewOrders(startDate, endDate);

      setLoadingOrders(false);
    };

    fetchData();
  }, [startDate, endDate, activeTab]);

  useEffect(() => {
    if (activeTab !== "history") {
      return;
    }

    const fetchHistoryOrders = async () => {
      try {
        setLoadingOrders(true);

        await getOrderHistory(historyStartDate, historyEndDate);
      } catch (error) {
        console.error("History tab load error:", error);

        toast.error("Unable to fetch order history");
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchHistoryOrders();
  }, [activeTab]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let isFetching = false;

    const handler = async (event) => {
      if (isFetching) {
        return;
      }

      isFetching = true;

      const res = await getNewOrders(startDate, endDate);

      const latestOrders = Array.isArray(res)
        ? res
        : Array.isArray(res?.response)
          ? res.response
          : Array.isArray(res?.data)
            ? res.data
            : [];

      if (event.type === "newOrderReceived" && latestOrders.length > 0) {
        const newIds = latestOrders
          .map((o) => o.orderid)
          .filter((id) => !orders.some((o) => o.orderid === id));

        if (newIds.length > 0) {
          setNewOrderIds([newIds[0]]);

          setTimeout(() => {
            setNewOrderIds([]);
          }, 3000);
        }
      }

      setTimeout(() => {
        isFetching = false;
      }, 500);
    };

    window.addEventListener("newOrderReceived", handler);

    window.addEventListener("orderUpdated", handler);

    return () => {
      window.removeEventListener("newOrderReceived", handler);

      window.removeEventListener("orderUpdated", handler);
    };
  }, [startDate, endDate, orders]);

  useEffect(() => {
    getDeliveryPersons();
  }, []);

  useEffect(() => {
    const getReceipt = async () => {
      try {
        const res = await fetchReceiptDetails(userID);

        const receipt =
          res?.response?.[0] ||
          res?.data?.[0] ||
          res?.response ||
          res?.data ||
          res ||
          null;

        if (receipt && typeof receipt === "object") {
          setreciptDetails(receipt);
        }
      } catch (err) {
        console.error("Receipt API error", err);
      }
    };

    if (userID) {
      getReceipt();
    }
  }, [userID]);

  useEffect(() => {
    const fetchStatus = async () => {
      const res = await getCatalogueStatus();

      if (res?.responseCode === "00") {
        const status = res.response.catalogueStatus;

        setOrderAccepting(status === "show");
      }
    };

    fetchStatus();
  }, []);

  return (
    <>
      <div className="p-6 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
        {cashModalOrder && (
          <CashCollectionModal
            order={cashModalOrder}
            onClose={() => setCashModalOrder(null)}
            onConfirm={handleCashCollect}
          />
        )}

        <div className="flex justify-between items-center mb-4 montserrat-font-1">
          <h1 className="text-2xl font-semibold">Orders Management</h1>

          <div className="flex items-center gap-3">
            <CreateBulkOrder userID={userID} receiptDetails={reciptDetails} />

            {String(userID || "") === "A1437" && (
              <button
                type="button"
                onClick={() => navigate("/orders/vendor-management")}
                className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-violet-700 transition"
              >
                <Store size={16} />
                Vendor
              </button>
            )}

            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-600 transition"
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            <button
              onClick={() => navigate("/orders/ordersettings")}
              className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded text-sm font-medium"
            >
              <Settings size={16} />
              Order Settings
            </button>

            <div className="flex items-center gap-2 bg-green-100 px-3 py-2 rounded-lg border">
              <span className="text-sm whitespace-nowrap">Order Accepting</span>

              <button
                onClick={handleCatalogueToggle}
                className={`w-10 h-5 flex items-center rounded-full p-0.5 transition-all duration-300 ${
                  orderAccepting ? "bg-green-500" : "bg-red-400"
                }`}
              >
                <div
                  className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-all duration-300 ${
                    orderAccepting ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>

              <span
                className={`text-xs font-semibold min-w-[28px] text-center ${
                  orderAccepting ? "text-green-600" : "text-gray-500"
                }`}
              >
                {orderAccepting ? "ON" : "OFF"}
              </span>
            </div>
          </div>
        </div>

        <OrderFilter
          activeTab={activeTab}
          filter={filter}
          setFilter={setFilter}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          search={search}
          setSearch={setSearch}
          paymentFilter={paymentFilter}
          setPaymentFilter={setPaymentFilter}
          orderTypeFilter={orderTypeFilter}
          setOrderTypeFilter={setOrderTypeFilter}
          historyStartDate={historyStartDate}
          setHistoryStartDate={setHistoryStartDate}
          historyEndDate={historyEndDate}
          setHistoryEndDate={setHistoryEndDate}
          handleHistorySearch={handleHistorySearch}
          loadingOrders={loadingOrders}
          filteredHistory={filteredHistory}
          exportAnyJsonToExcel={exportAnyJsonToExcel}
          today={today}
          setSelectedSummaryDate={setSelectedSummaryDate}
          setShowSummaryModal={setShowSummaryModal}
          setSelectedMergedDate={setSelectedMergedDate}
          setShowMergedModal={setShowMergedModal}
        />

        <div className="flex gap-6 border-b mb-4 montserrat-font-1">
          <button
            onClick={() => {
              setActiveTab("current");

              setFilter("all");
            }}
            className={`pb-2 text-sm font-medium ${
              activeTab === "current"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-500"
            }`}
          >
            Current Order
          </button>

          <button
            onClick={() => {
              setActiveTab("schedule");

              setFilter("all");
            }}
            className={`pb-2 text-sm font-medium ${
              activeTab === "schedule"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-500"
            }`}
          >
            Schedule
          </button>

          <button
            onClick={() => {
              setActiveTab("history");

              setFilter("all");
            }}
            className={`pb-2 text-sm font-medium ${
              activeTab === "history"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-500"
            }`}
          >
            Order History
          </button>
        </div>

        <div className="bg-white rounded overflow-auto relative montserrat-font-1">
          {activeTab === "current" && (
            <CurrentOrdersTable
              filter={filter}
              filteredOrders={sortedOrders}
              dispatchedOrderIds={dispatchedOrderIds}
              newOrderIds={newOrderIds}
              handleRowClickCurrent={handleRowClickCurrent}
              openRowCurrent={openRowCurrent}
              orderDetails={orderDetails}
              orderDetailsLoading={orderDetailsLoading}
              selectedDeliveryPerson={selectedDeliveryPerson}
              getTimeElapsed={getTimeElapsed}
              handlePrintClick={handlePrintClick}
              handleDirectClick={handleDirectClick}
              setActionModalOrder={setActionModalOrder}
              setCancelModalOrder={setCancelModalOrder}
              reciptDetails={reciptDetails}
              KitchenPrint={KitchenPrint}
              getDiningStatusLabel={getDiningStatusLabel}
              handleCancel={handleCancel}
              setCashModalOrder={setCashModalOrder}
              updateOrderStatus={updateOrderStatus}
              getNewOrders={getNewOrders}
              getOrderHistory={getOrderHistory}
              startDate={startDate}
              endDate={endDate}
              getOrderDetails={getOrderDetails}
              loadingOrders={loadingOrders}
              setTakeawayOrder={setTakeawayOrder}
              isScheduleOrder={isScheduleOrder}
            />
          )}

          {activeTab === "schedule" && (
            <Schedule
              filter={filter}
              filteredOrders={scheduleOrders}
              newOrderIds={newOrderIds}
              handleRowClickCurrent={handleRowClickCurrent}
              openRowCurrent={openRowCurrent}
              orderDetails={orderDetails}
              orderDetailsLoading={orderDetailsLoading}
              selectedDeliveryPerson={selectedDeliveryPerson}
              getTimeElapsed={getTimeElapsed}
              handlePrintClick={handlePrintClick}
              handleDirectClick={handleDirectClick}
              setActionModalOrder={setActionModalOrder}
              setCancelModalOrder={setCancelModalOrder}
              reciptDetails={reciptDetails}
              KitchenPrint={KitchenPrint}
              getDiningStatusLabel={getDiningStatusLabel}
              handleCancel={handleCancel}
              setCashModalOrder={setCashModalOrder}
              updateOrderStatus={updateOrderStatus}
              getNewOrders={getNewOrders}
              getOrderHistory={getOrderHistory}
              startDate={startDate}
              endDate={endDate}
              getOrderDetails={getOrderDetails}
              loadingOrders={loadingOrders}
              setTakeawayOrder={setTakeawayOrder}
            />
          )}

          {activeTab === "history" && (
            <>
              {loadingOrders ? (
                <div className="flex min-h-[320px] items-center justify-center bg-white">
                  <LoadingScreen text="Loading order history..." />
                </div>
              ) : (
                <OrderHistoryTable
                  handleRowClickHistory={handleRowClickHistory}
                  openRowHistory={openRowHistory}
                  orderDetails={orderDetails}
                  orderDetailsLoading={orderDetailsLoading}
                  selectedDeliveryPerson={selectedDeliveryPerson}
                  KitchenPrint={KitchenPrint}
                  reciptDetails={reciptDetails}
                  handleMarkDelivered={openDeliverModal}
                  orderHistory={filteredHistory}
                  loadingOrders={loadingOrders}
                  handleImageClick={handleDownloadPNG}
                  handlePrintClick={handlePrintClick}
                />
              )}
            </>
          )}

          <DispatchOrderModel
            order={actionModalOrder}
            onClose={() => setActionModalOrder(null)}
            updateOrderStatus={updateOrderStatus}
            deliveryPersons={deliveryPersons || []}
            assignDeliveryPartner={assignDeliveryPartner}
            sendReceipt={handleSendReceipt}
            getCatalogOrder={() => getNewOrders(startDate, endDate)}
            getDeliveryPersons={getDeliveryPersons}
            notifyDriver={notifyDriver}
            updateScheduleOrder={updateScheduleOrder}
            userID={userID}
          />

          {takeawayOrder && (
            <TakeawayModal
              order={takeawayOrder}
              onClose={() => setTakeawayOrder(null)}
              updateOrderStatus={updateOrderStatus}
              getCatalogOrder={() => {}}
              userID={userID}
            />
          )}

          <OrderActionModals
            deliverModalOrder={deliverModalOrder}
            setDeliverModalOrder={setDeliverModalOrder}
            handleConfirmDelivered={handleConfirmDelivered}
            showSummaryModal={showSummaryModal}
            setShowSummaryModal={setShowSummaryModal}
            selectedSummaryDate={selectedSummaryDate}
            setSelectedSummaryDate={setSelectedSummaryDate}
            summaryActionLoading={summaryActionLoading}
            handlePrintSummary={handlePrintSummary}
            handleDownloadSummaryExcel={handleDownloadSummaryExcel}
            showMergedModal={showMergedModal}
            setShowMergedModal={setShowMergedModal}
            selectedMergedDate={selectedMergedDate}
            setSelectedMergedDate={setSelectedMergedDate}
            mergedSummaryActionLoading={mergedSummaryActionLoading}
            handlePrintMergedSummary={handlePrintMergedSummary}
            handleDownloadMergedSummaryExcel={handleDownloadMergedSummaryExcel}
            cancelModalOrder={cancelModalOrder}
            setCancelModalOrder={setCancelModalOrder}
            openCancelDropdown={openCancelDropdown}
            setOpenCancelDropdown={setOpenCancelDropdown}
            cancelReason={cancelReason}
            setCancelReason={setCancelReason}
            cancelReasons={cancelReasons}
            userID={userID}
            updateOrderStatus={updateOrderStatus}
          />
        </div>
      </div>
    </>
  );
};

export default OrdersPage;
